import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2, Plus } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  createManagedFaxType,
  listManagedFaxTypes,
  updateManagedFaxType,
  type ManagedFaxType,
} from '../../services/faxTypes';

/**
 * The document type list every agency picks from.
 *
 * Roger's list rather than each agency's: a type only earns its place by
 * meaning the same thing everywhere, and an agency that could rename Orders
 * would make every cross-agency question unanswerable. Tags exist for the
 * vocabulary one office actually needs.
 *
 * A type is retired, never deleted. Faxes already filed under one still have
 * to say what they are, so the row stays and simply stops being offered. The
 * count is here so that is an informed decision rather than a guess.
 */
export function FaxTypesPanel() {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [types, setTypes] = useState<ManagedFaxType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await listManagedFaxTypes());
      setLoadFailed(false);
    } catch (error) {
      setTypes([]);
      setLoadFailed(true);
      console.error('Failed to load the fax types', error);
      showError('Could not load the fax types.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    const wanted = newName.trim();
    if (!wanted) return;
    setSaving(true);
    try {
      // Added at the end rather than sorted in: the useful order is how often
      // a type gets picked, which is not alphabetical, and somebody can move
      // it afterwards.
      const last = types.reduce(
        (high, type) => Math.max(high, type.sort_order),
        0
      );
      await createManagedFaxType(wanted, last + 10);
      setNewName('');
      showSuccess(`Added ${wanted}.`);
      await load();
    } catch (error) {
      console.error('Failed to add the fax type', error);
      showError('Could not add that type.');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (type: ManagedFaxType, isActive: boolean) => {
    setSaving(true);
    try {
      await updateManagedFaxType(type.id, { is_active: isActive });
      showSuccess(
        isActive ? `Restored ${type.name}.` : `Retired ${type.name}.`
      );
      await load();
    } catch (error) {
      console.error('Failed to update the fax type', error);
      showError('Could not update that type.');
    } finally {
      setSaving(false);
    }
  };

  const move = async (type: ManagedFaxType, direction: -1 | 1) => {
    const ordered = [...types].sort((a, b) => a.sort_order - b.sort_order);
    const index = ordered.findIndex(candidate => candidate.id === type.id);
    const swapWith = ordered[index + direction];
    if (!swapWith) return;
    setSaving(true);
    try {
      // Swapped rather than renumbered, so one move is two writes however long
      // the list is.
      await updateManagedFaxType(type.id, { sort_order: swapWith.sort_order });
      await updateManagedFaxType(swapWith.id, { sort_order: type.sort_order });
      await load();
    } catch (error) {
      console.error('Failed to reorder the fax types', error);
      showError('Could not reorder the types.');
    } finally {
      setSaving(false);
    }
  };

  const ordered = [...types].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fax document types</CardTitle>
        <CardDescription>
          The list every agency picks from when saying what a fax is. Shared on
          purpose, so the same word means the same thing everywhere. Agencies
          add their own vocabulary as tags instead.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='flex flex-col gap-2 sm:flex-row'>
          <Input
            aria-label='New type name'
            placeholder='A kind of document'
            className='sm:flex-1'
            value={newName}
            disabled={saving}
            onChange={event => setNewName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && newName.trim()) void add();
            }}
          />
          <Button
            onClick={() => void add()}
            disabled={saving || !newName.trim()}
            className='whitespace-nowrap'
          >
            <Plus className='mr-2 h-4 w-4' />
            Add type
          </Button>
        </div>

        {loading ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        ) : loadFailed ? (
          <div className='space-y-3 py-10 text-center'>
            <p className='text-destructive'>Could not load the fax types.</p>
            <Button variant='outline' onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : ordered.length === 0 ? (
          <p className='py-10 text-center text-muted-foreground'>
            No types yet.
          </p>
        ) : (
          <div className='overflow-x-auto'>
            <Table className='min-w-[560px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Faxes</TableHead>
                  <TableHead>Offered</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordered.map((type, index) => (
                  <TableRow key={type.id}>
                    <TableCell className='font-medium'>{type.name}</TableCell>
                    <TableCell>{type.usage_count}</TableCell>
                    <TableCell>
                      {type.is_active ? (
                        <Badge>Offered</Badge>
                      ) : (
                        <Badge variant='outline'>Retired</Badge>
                      )}
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='flex justify-end gap-1'>
                        <Button
                          variant='ghost'
                          size='sm'
                          aria-label={`Move ${type.name} up`}
                          disabled={saving || index === 0}
                          onClick={() => void move(type, -1)}
                        >
                          Up
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          aria-label={`Move ${type.name} down`}
                          disabled={saving || index === ordered.length - 1}
                          onClick={() => void move(type, 1)}
                        >
                          Down
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          aria-label={
                            type.is_active
                              ? `Retire ${type.name}`
                              : `Restore ${type.name}`
                          }
                          disabled={saving}
                          onClick={() => void setActive(type, !type.is_active)}
                        >
                          {type.is_active ? 'Retire' : 'Restore'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FaxTypesPanel;
